import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * Returns whether the global Rovo button should be shown for the current user.
 *
 * This is a copy of `@jira/ai-rovo__button/src/useShouldShowRovoButton.tsx` kept here
 * so that L2 packages (e.g. actions-route-provider) can consume it without depending on
 * the L3 `@jira/ai-rovo__button` package.
 *
 * When updating this file, keep it in sync with the original.
 * Tracked in: TODO — replace usages in @jira/ai-rovo__button with an import from here.
 */
export const useShouldShowRovoButton = (): boolean => {
	const { data: rovoEntitlementData } = useRovoEntitlementResource();
	const isRovoLLMEnabled = rovoEntitlementData?.isRovoLLMEnabled ?? false;
	const licenseState = useLicenseStates();
	const { hasSoftwareAccess, hasProductDiscoveryAccess, hasServiceDeskAccess } =
		useApplicationPermissions();
	const { data: projectContextData } = useProjectContext();
	const projectType = projectContextData?.projectType;

	const softwareAccessEnabled = licenseState.software === LICENSE_STATE_ACTIVE && hasSoftwareAccess;
	const isProductDiscoveryEnabled =
		licenseState.productDiscovery === LICENSE_STATE_ACTIVE && hasProductDiscoveryAccess;
	const isServiceDeskEnabled =
		licenseState.serviceDesk === LICENSE_STATE_ACTIVE &&
		hasServiceDeskAccess &&
		fg('jsm_asap_ai_rovo_button');

	if (
		!isRovoLLMEnabled ||
		(!softwareAccessEnabled && !isProductDiscoveryEnabled && !isServiceDeskEnabled)
	) {
		return false;
	}

	// Exclude customer service page
	if (expVal('jira_free_gen_editor', 'isEnabled', false)) {
		return !(projectType === 'customer_service');
	}

	if (projectType === 'customer_service') {
		return false;
	}

	return true;
};
